'use client'

import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Image from 'next/image'
import { RootState } from 'features/store'
import { setProfileSettingsModal } from 'features/generalSlice'
import { setUser } from 'features/authSlice'
import { useUpdateProfileMutation } from 'graphql/generated/graphql'
import ProfileCompletedModal from '@/components/modals/ProfileCompletedModal'

const COMPLETED_DATE_KEY = 'culineer_profile_completed_date'
const LOCATION_OPT_OUT_KEY = 'culineer_location_opted_out'
const WIDGET_DISMISSED_KEY = 'culineer_profile_widget_dismissed'
const MODAL_SHOWN_KEY = 'culineer_profile_completed_modal_shown'
const COMPLETED_WIDGET_DAYS = 5

const ProfileCompletionWidget = () => {
    const dispatch = useDispatch()
    const user = useSelector((state: RootState) => state.culineerAuth.user)
    const [showModal, setShowModal] = useState(false)
    const [locationOptedOut, setLocationOptedOut] = useState(false)
    const [dismissed, setDismissed] = useState(false)
    const [updateProfile] = useUpdateProfileMutation()
    const serverDismissed = !!user?.profile_widget_dismissed
    const prevCompleteRef = useRef<boolean | null>(null)

    // Load opt-out and dismissed state from localStorage on mount
    useEffect(() => {
        if (typeof window !== 'undefined') {
            setLocationOptedOut(localStorage.getItem(LOCATION_OPT_OUT_KEY) === 'true')
            setDismissed(localStorage.getItem(WIDGET_DISMISSED_KEY) === 'true')
        }
    }, [])

    const userType = user?.user_type?.toLowerCase()
    const isBusinessUser = userType === 'producer' || userType === 'market' || userType === 'business'

    // Location step is complete if they saved city/state OR explicitly opted out
    const locationDone = !!user?.location_completed || locationOptedOut || (user?.location_city && user?.location_state)

    const steps = [
        { id: 'image', label: 'Add profile photo', completed: !!user?.profile_image_completed },
        { id: 'bio', label: 'Add bio', completed: !!user?.bio_completed },
        { id: 'prefs', label: 'Set notification preference', completed: !!user?.communication_completed },
        { id: 'location', label: 'Add your location', completed: locationDone },
    ]

    const completedCount = steps.filter(s => s.completed).length
    const isFullyComplete = completedCount === steps.length
    const progress = (completedCount / steps.length) * 100

    // Detect transition to fully complete → show congrats modal once, ever
    useEffect(() => {
        // On first run, just record current state — don't trigger modal
        if (prevCompleteRef.current === null) {
            prevCompleteRef.current = isFullyComplete
            if (
                isFullyComplete &&
                typeof window !== 'undefined' &&
                !localStorage.getItem(COMPLETED_DATE_KEY)
            ) {
                // Already complete the first time this widget ever mounts -- backfill
                // the completion date so the grace-period countdown can actually
                // start instead of never expiring.
                localStorage.setItem(COMPLETED_DATE_KEY, Date.now().toString())
            }
            return
        }
        if (prevCompleteRef.current === false && isFullyComplete) {
            const alreadyShown = typeof window !== 'undefined' &&
                localStorage.getItem(MODAL_SHOWN_KEY) === 'true'
            if (!alreadyShown) {
                localStorage.setItem(COMPLETED_DATE_KEY, Date.now().toString())
                localStorage.setItem(MODAL_SHOWN_KEY, 'true')
                setShowModal(true)
            }
        }
        prevCompleteRef.current = isFullyComplete
    }, [isFullyComplete])

    // Save the dismissal on the account so it follows the user to other browsers/devices.
    const persistDismissed = () => {
        if (!user?.user_id || user.profile_widget_dismissed) return
        dispatch(setUser({ profile_widget_dismissed: true }))
        updateProfile({
            variables: { userID: user.user_id, input: { profile_widget_dismissed: true } as any },
        }).catch((error) => console.error('Failed to save widget dismissal:', error))
    }

    // Dismissed earlier in this browser but never saved to the account -- save it now.
    useEffect(() => {
        if (dismissed) persistDismissed()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dismissed, user?.user_id])

    // Start the 5-day "Profile Completed" countdown on the account, once, so a new browser or
    // device doesn't restart it. Reuse this browser's date if it has one so a running
    // countdown isn't extended.
    useEffect(() => {
        if (!isFullyComplete || !user?.user_id || user.profile_widget_completed_at || typeof window === 'undefined') return
        const local = parseInt(localStorage.getItem(COMPLETED_DATE_KEY) || '', 10)
        const stamp = new Date(Number.isFinite(local) ? local : Date.now()).toISOString()
        dispatch(setUser({ profile_widget_completed_at: stamp }))
        updateProfile({
            variables: { userID: user.user_id, input: { profile_widget_completed_at: stamp } as any },
        }).catch((error) => console.error('Failed to save profile completion date:', error))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFullyComplete, user?.user_id, user?.profile_widget_completed_at])

    const handleDismiss = () => {
        localStorage.setItem(WIDGET_DISMISSED_KEY, 'true')
        setDismissed(true)
        persistDismissed()
    }

    // Don't show for business users, unauthenticated, or dismissed
    if (!user || isBusinessUser || dismissed || serverDismissed) return null

    const completedDateRaw = typeof window !== 'undefined'
        ? localStorage.getItem(COMPLETED_DATE_KEY)
        : null
    // The account's date wins (so every browser agrees); fall back to this browser's.
    const accountCompletedAt = user?.profile_widget_completed_at
        ? new Date(user.profile_widget_completed_at).getTime()
        : NaN
    const completedDate = Number.isFinite(accountCompletedAt)
        ? accountCompletedAt
        : completedDateRaw ? parseInt(completedDateRaw, 10) : null
    const daysSinceCompletion = completedDate
        ? (Date.now() - completedDate) / (1000 * 60 * 60 * 24)
        : null

    // --- Fully complete state ---
    if (isFullyComplete) {
        if (!completedDate || daysSinceCompletion <= COMPLETED_WIDGET_DAYS) {
            return (
                <>
                    <ProfileCompletedModal open={showModal} onClose={() => setShowModal(false)} />
                    <div className="relative mb-4 rounded-xl border border-[#E6E6E6] bg-white p-3 shadow-sm">
                        <div className="mb-1 flex items-start justify-between gap-2">
                            <div className="flex min-w-0 flex-1 items-center gap-3">
                                <Image
                                    src="/assets/icons/profile-complete-badge.png"
                                    alt="Profile complete"
                                    width={40}
                                    height={40}
                                    className="flex-shrink-0"
                                />
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">Profile Completed</p>
                                    <p className="text-xs text-gray-500">You're all set! Your Culineer experience is now personalized.</p>
                                </div>
                            </div>
                            <button
                                onClick={handleDismiss}
                                className="relative z-10 flex-shrink-0 text-gray-300 hover:text-gray-500"
                                aria-label="Dismiss"
                            >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </>
            )
        }
        return null
    }

    // --- In-progress widget ---
    return (
        <>
            <ProfileCompletedModal open={showModal} onClose={() => setShowModal(false)} />
            <div className="relative mb-4 rounded-xl border border-[#D2D2D2] bg-white p-3 shadow-sm">
                {/* Header */}
                <div className="mb-2 flex items-center gap-2">
                    <span className="min-w-0 flex-1 font-['Inter'] text-base font-semibold leading-[150%] tracking-[-0.02em] text-[#202020]">
                        Complete Profile
                    </span>
                    <span className="flex-shrink-0 font-['Inter'] text-base font-normal leading-[150%] tracking-[-0.02em] text-[#6B6B6B]">
                        Step {completedCount} of {steps.length}
                    </span>
                    <button
                        onClick={handleDismiss}
                        className="relative z-10 flex-shrink-0 text-gray-300 hover:text-gray-500"
                        aria-label="Dismiss"
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                {/* Progress bar */}
                <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                        className="h-full rounded-full bg-[#85C349] transition-all duration-500"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                {/* Checklist */}
                <ul className="mb-4 space-y-2.5">
                    {steps.map((step) => (
                        <li key={step.id}>
                            <div className="flex items-center gap-2.5">
                                {step.completed ? (
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="flex-shrink-0">
                                        <circle cx="9" cy="9" r="9" fill="#85C349" />
                                        <path d="M5.5 9l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="flex-shrink-0">
                                        <circle cx="9" cy="9" r="8" stroke="#D1D5DB" strokeWidth="1.5" fill="none"/>
                                    </svg>
                                )}
                                <span className={`text-xs ${step.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                                    {step.label}
                                </span>
                            </div>

                        </li>
                    ))}
                </ul>

                {/* CTA Button */}
                <button
                    onClick={() => dispatch(setProfileSettingsModal(true))}
                    className="w-full rounded-full bg-[#85C349] py-2 text-xs font-semibold text-white transition hover:bg-[#75B339]"
                >
                    Continue setup
                </button>
            </div>
        </>
    )
}

export default ProfileCompletionWidget
